/**
 * Custom hook que centraliza o estado do sidebar/drawer:
 * - submenus (cadastros, finance, settings) abertos conforme a URL atual
 * - collapsed/menuOpen persistidos em localStorage
 * - tema (light/dark) refletido no <html data-theme=...>
 * - perms carregadas de /permissions/me
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const LS_KEYS = { collapsed: "ccp_sidebar_collapsed", theme: "ccp_theme" };

function readLS(key, fallback) {
  try { return localStorage.getItem(key); } catch (err) { console.warn("localStorage.getItem", key, err); return fallback; }
}

function writeLS(key, value) {
  try { localStorage.setItem(key, value); } catch (err) { console.warn("localStorage.setItem", key, err); }
}

export default function useLayoutState(token, user) {
  const location = useLocation();

  const [cadastrosOpen, setCadastrosOpen] = useState(
    location.pathname.startsWith("/products") ||
    location.pathname.startsWith("/customers") ||
    location.pathname.startsWith("/cadastros")
  );
  const [financeOpen, setFinanceOpen] = useState(location.pathname.startsWith("/financeiro"));
  const [reportsOpen, setReportsOpen] = useState(location.pathname.startsWith("/relatorios"));
  const [fiscalOpen, setFiscalOpen] = useState(location.pathname.startsWith("/fiscal"));
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/settings") || location.pathname.startsWith("/backup")
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readLS(LS_KEYS.collapsed) === "1");
  const [theme, setTheme] = useState(() => (readLS(LS_KEYS.theme) === "dark" ? "dark" : "light"));
  const [perms, setPerms] = useState(null);

  // Fecha drawer sempre que navegar
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Reflete o tema no root
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Carrega permissões
  useEffect(() => {
    if (!token) return;
    api.get("/permissions/me").then(({ data }) => setPerms(data.permissions || {})).catch(() => setPerms({}));
  }, [token]);

  const toggleTheme = () => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      writeLS(LS_KEYS.theme, next);
      return next;
    });
  };

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      writeLS(LS_KEYS.collapsed, next ? "1" : "0");
      return next;
    });
  };

  const isAdminReal = user?.role === "admin" && !user?.is_station;
  const hasPerm = (key) => {
    if (!key) return true;
    // Estações têm permissões PRÓPRIAS — role=admin não faz bypass para elas.
    if (isAdminReal) return true;
    if (!perms) return true; // enquanto carrega, mostra tudo para evitar flash
    return !!perms[key];
  };

  return {
    // submenus
    cadastrosOpen, setCadastrosOpen,
    reportsOpen, setReportsOpen,
    financeOpen, setFinanceOpen,
    fiscalOpen, setFiscalOpen,
    settingsOpen, setSettingsOpen,
    // drawer
    menuOpen, setMenuOpen,
    // collapsed
    collapsed, toggleCollapsed,
    // theme
    theme, toggleTheme,
    // perms
    hasPerm, isAdminReal,
  };
}
